// Notification bell + popover used in the desktop header.
// Shows an unread dot when there are new completions / credit events,
// lists the most recent items, and deep-links each one to its destination.
import { Bell, Sparkles, Coins, AlertTriangle, Wand2, Megaphone, Crown, XCircle } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  useNotifications,
  type AppNotification,
  type NotificationKind,
} from "@/hooks/useNotifications";
import { useSignedImage } from "@/hooks/useSignedImage";

const NotificationThumb = ({ path }: { path: string }) => {
  // The stored value can be either a private storage path (newer rows)
  // or a full https URL (legacy). useSignedImage handles both.
  const url = useSignedImage("tryon-results", path);
  if (!url) {
    return (
      <span className="h-10 w-10 shrink-0 rounded-md border border-white/10 bg-white/5" />
    );
  }
  return (
    <img
      src={url}
      alt=""
      className="h-10 w-10 shrink-0 rounded-md border border-white/10 object-cover"
      loading="lazy"
    />
  );
};

const ICONS: Record<NotificationKind, React.ComponentType<{ className?: string }>> = {
  tryon_ready: Sparkles,
  aura_ready: Wand2,
  tryon_failed: AlertTriangle,
  credits_granted: Coins,
  credits_purchased: Coins,
  broadcast: Megaphone,
  featured_approved: Crown,
  featured_rejected: XCircle,
};

const ICON_TONE: Record<NotificationKind, string> = {
  tryon_ready: "bg-primary/15 text-primary",
  aura_ready: "bg-accent/15 text-accent",
  tryon_failed: "bg-warning/15 text-warning",
  credits_granted: "bg-success/15 text-success",
  credits_purchased: "bg-success/15 text-success",
  broadcast: "bg-primary/15 text-primary",
  featured_approved: "bg-primary/15 text-primary",
  featured_rejected: "bg-warning/15 text-warning",
};

const formatRelative = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const { items, unreadCount, loading, markAllRead, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && unreadCount > 0) {
      // Mark as seen as soon as the panel opens.
      markAllRead();
    }
  };

  const handleClick = (n: AppNotification) => {
    setOpen(false);
    if (n.href) navigate(n.href);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-card/40 text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground shadow-[0_0_12px_-2px_hsl(var(--primary))]">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[min(360px,calc(100vw-24px))] border-white/10 bg-card/95 p-0 backdrop-blur-xl"
      >
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
          <span className="font-display text-sm font-semibold">Notifications</span>
          {items.length > 0 && (
            <button
              type="button"
              onClick={() => { clearAll(); }}
              className="text-[11px] font-semibold uppercase tracking-wider text-primary transition-colors hover:text-primary/80"
            >
              Clear all
            </button>
          )}
        </div>

        <ScrollArea className="max-h-[420px]">
          {loading ? (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Bell className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground">You're all caught up</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Try-on results, Aura scores and credit updates will appear here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((n) => {
                const Icon = ICONS[n.kind];
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleClick(n)}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
                    >
                      <span
                        className={cn(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                          ICON_TONE[n.kind],
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {n.title}
                        </span>
                        {n.body && (
                          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                            {n.body}
                          </span>
                        )}
                        <span className="mt-1 block text-[10px] uppercase tracking-wider text-muted-foreground/70">
                          {formatRelative(n.createdAt)}
                        </span>
                      </span>
                      {n.imageUrl && <NotificationThumb path={n.imageUrl} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
